<template>
  <!-- A thumbnail-sized sketch of what a step looks like on screen. Built from
       the same primitives as the popup preview (a window, a toolbar, swatch
       cards) at a size where only the silhouette survives — enough to tell the
       four steps apart at a glance, not enough to compete with the copy beside
       it. Purely decorative; the step's own heading says what happens. -->
  <span class="block h-14 w-24 select-none" aria-hidden="true">
    <!-- 1. A page open in a tab. -->
    <span v-if="variant === 'page'" class="flex h-full w-full flex-col overflow-hidden rounded-md border border-gray-800 bg-gray-900">
      <span class="flex items-center gap-1 border-b border-gray-800 px-1.5 py-1">
        <span class="h-1 w-1 rounded-full bg-gray-700"></span>
        <span class="h-1 w-1 rounded-full bg-gray-700"></span>
        <span class="ml-0.5 h-1.5 flex-1 rounded-full bg-gray-800"></span>
      </span>
      <span class="flex flex-1 flex-col justify-center gap-1 px-2">
        <span class="block h-1 w-3/4 rounded-full bg-gray-700"></span>
        <span class="block h-1 w-1/2 rounded-full bg-gray-800"></span>
        <span class="block h-1 w-2/3 rounded-full bg-gray-800"></span>
      </span>
    </span>

    <!-- 2. The toolbar, with the pinned icon lit. -->
    <span v-else-if="variant === 'toolbar'" class="flex h-full w-full items-center justify-center rounded-md border border-gray-800 bg-gray-900">
      <span class="flex items-center gap-1.5 rounded-full bg-gray-950 px-2 py-1.5">
        <span class="h-2.5 w-2.5 rounded-sm bg-gray-800"></span>
        <span class="h-2.5 w-2.5 rounded-sm bg-gray-800"></span>
        <span class="h-4 w-4 rounded-sm bg-indigo-500/30 ring-1 ring-indigo-400"></span>
      </span>
    </span>

    <!-- 3. Swatches, one of them copied. -->
    <span v-else-if="variant === 'swatch'" class="flex h-full w-full items-end gap-1 rounded-md border border-gray-800 bg-gray-900 p-1.5">
      <span
        v-for="color in swatchColors"
        :key="color"
        class="flex-1 overflow-hidden rounded-sm bg-white"
      >
        <span class="block h-5" :style="{ backgroundColor: color }"></span>
        <span class="block h-1.5"></span>
      </span>
    </span>

    <!-- 4. The hand-off: the popup, then the site. -->
    <span v-else class="flex h-full w-full items-center gap-1 rounded-md border border-gray-800 bg-gray-900 p-1.5">
      <span class="flex h-full w-6 flex-col gap-0.5 rounded-sm border border-gray-800 p-0.5">
        <span class="block flex-1 rounded-[1px] bg-gray-800"></span>
        <span class="block flex-1 rounded-[1px] bg-gray-800"></span>
      </span>
      <svg class="h-3 w-3 shrink-0 text-indigo-400" viewBox="0 0 16 16" fill="currentColor">
        <path fill-rule="evenodd" d="M2 8c0 .414.336.75.75.75h8.69l-1.22 1.22a.75.75 0 1 0 1.06 1.06l2.5-2.5a.75.75 0 0 0 0-1.06l-2.5-2.5a.75.75 0 1 0-1.06 1.06l1.22 1.22H2.75A.75.75 0 0 0 2 8Z" clip-rule="evenodd" />
      </svg>
      <span class="grid h-full flex-1 grid-cols-3 grid-rows-2 gap-0.5 rounded-sm border border-gray-800 p-0.5">
        <span v-for="color in handoffColors" :key="color" class="rounded-[1px]" :style="{ backgroundColor: color }"></span>
      </span>
    </span>
  </span>
</template>

<script setup>
defineProps({
  variant: { type: String, required: true },
})

const swatchColors = ['#4F46E5', '#0EA5E9', '#F97316']
const handoffColors = ['#4F46E5', '#0EA5E9', '#F97316', '#22C55E', '#7C3AED', '#E2E8F0']
</script>
